import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import type { FlowDocument, StepNode } from '@segmently/schema-models/v2';
import { CoreFirebaseService } from './CoreFirebaseService';

export type ArticleDraftStatus = 'draft' | 'published' | 'retired';
export type ArticleRepresentationProfile = 'standard' | 'phone' | 'tablet' | 'laptop';
/**
 * Per-article editor hosting mode: 'screen' = the classic fullscreen
 * single-screen editor (default; legacy drafts without the field behave
 * exactly as before), 'flow' = the multi-screen ReactFlow canvas.
 * A document with >1 screens always opens in Flow regardless of this value
 * (the single-screen editor cannot represent multi-screen documents).
 */
export type ArticleEditorMode = 'screen' | 'flow';

/** A live (published, not retired) article: its alias and title from the site index, its draft id when it has one. */
export interface LiveArticle {
  articleId: string | null;
  alias: string;
  title: string;
}

export interface ArticleDraft {
  id: string;
  projectId: string;
  title: string;
  alias: string;
  status: ArticleDraftStatus;
  defaultLanguage: string;
  representationProfile: ArticleRepresentationProfile;
  flowDocument: FlowDocument;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
  publishedAt?: string;
  /** Set by `segmently content-plan articles retire` (G3); kept by saves. A later publishedAt means republished. */
  retiredAt?: string;
  /** Customer-facing article URL. Prefer the branded article custom domain. */
  publishedUrl?: string;
  /** Direct static HTML asset URL under assets/articles, kept for verification/debugging. */
  assetUrl?: string;
  configUrl?: string;
  gatewayUrl?: string;
  customDomainUrl?: string;
  webShellVersion?: string;
  /**
   * Folder assignment (projects/{pid}/articleFolders). `null` = root.
   * Legacy docs lack the field entirely — filter in memory with
   * `!draft.folderId`, never with a Firestore `== null` query.
   */
  folderId?: string | null;
  /**
   * `true` once the user renamed the article explicitly (updateDetails).
   * While falsy, `saveFlowDocument` keeps deriving the title from the first
   * Text section — legacy docs (field absent) behave exactly as before.
   */
  titleManuallySet?: boolean;
  /**
   * Editor hosting mode. Normalized on read: legacy docs (field absent)
   * resolve to 'screen' in memory — no backfill write.
   */
  editorMode?: ArticleEditorMode;
}

export interface CreateArticleDraftInput {
  title?: string;
  alias?: string;
  defaultLanguage?: string;
  createdBy?: string | null;
  flowDocument?: FlowDocument;
  folderId?: string | null;
}

export interface CloneArticleDraftInput {
  title?: string;
  alias?: string;
  createdBy?: string | null;
}

export interface SavePublishedArticleInput {
  url: string;
  assetUrl?: string;
  configUrl?: string;
  gatewayUrl?: string;
  customDomainUrl?: string;
  alias?: string;
  webShellVersion?: string;
}

export interface UpdateArticleDetailsInput {
  /** New display title. Empty / whitespace = "not provided" (partial write). */
  title?: string;
  /** New public alias (slug-normalized on write). Empty = "not provided". */
  alias?: string;
  /** Editor hosting mode. Absent = "not provided" (partial write). */
  editorMode?: ArticleEditorMode;
}

const COLLECTION_NAME = 'articles';
/** Builder-owned (written only by ArticlePublishService.publish / `articles retire`); the frontend only reads ids. */
const SITE_INDEX_COLLECTION = 'articleSiteIndex';
const DEFAULT_ARTICLE_TITLE = 'Untitled article';
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_ARTICLE_BACKGROUND = '#F7F8FA';
const DEFAULT_ARTICLE_PRESENTATION = {
  version: 1,
  profiles: {
    standard: {
      containerMaxWidth: 1120,
      contentInsets: { top: 48, end: 32, bottom: 56, start: 32 },
      sectionPaddingScale: 1,
      textScale: 1,
    },
    laptop: {
      containerMaxWidth: 960,
      contentInsets: { top: 44, end: 40, bottom: 52, start: 40 },
      sectionPaddingScale: 0.96,
      textScale: 0.96,
    },
    tablet: {
      containerMaxWidth: 760,
      contentInsets: { top: 36, end: 28, bottom: 44, start: 28 },
      sectionPaddingScale: 0.9,
      textScale: 0.92,
      titleTextScale: 0.9,
    },
    phone: {
      containerMaxWidth: '100%',
      contentInsets: { top: 28, end: 16, bottom: 36, start: 16 },
      sectionPaddingScale: 0.82,
      textScale: 0.88,
      titleTextScale: 0.82,
    },
  },
} as const;

export class ArticleDraftService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  async createDraft(projectId: string, input: CreateArticleDraftInput = {}): Promise<ArticleDraft> {
    const collectionRef = collection(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`);
    const docRef = doc(collectionRef);
    const now = new Date().toISOString();
    const title = normalizeTitle(input.title);
    const defaultLanguage = normalizeLanguage(input.defaultLanguage);
    const alias = normalizeAlias(input.alias || `${slugify(title)}-${docRef.id.slice(0, 6)}`);
    const flowDocument = input.flowDocument
      ? normalizeArticleFlowDocument(input.flowDocument, { projectId, articleId: docRef.id, alias, defaultLanguage })
      : createDefaultArticleFlowDocument({
          projectId,
          articleId: docRef.id,
          title,
          alias,
          defaultLanguage,
        });

    const draft: ArticleDraft = {
      id: docRef.id,
      projectId,
      title: deriveArticleTitle(flowDocument) || title,
      alias,
      status: 'draft',
      defaultLanguage,
      representationProfile: 'standard',
      flowDocument,
      createdAt: now,
      updatedAt: now,
      createdBy: input.createdBy ?? CoreFirebaseService.getInstance().getCurrentUserId(),
      // Explicit null (not undefined) so the field is actually written.
      folderId: input.folderId ?? null,
    };

    await setDoc(docRef, cleanForFirestore(draft));
    return draft;
  }

  async listDrafts(projectId: string): Promise<ArticleDraft[]> {
    const collectionRef = collection(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`);
    const snapshot = await getDocs(query(collectionRef, orderBy('updatedAt', 'desc')));
    return snapshot.docs.map((docSnap) => fromFirestore(projectId, docSnap.id, docSnap.data()));
  }

  async getDraft(projectId: string, articleId: string): Promise<ArticleDraft | null> {
    const docRef = doc(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`, articleId);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    return fromFirestore(projectId, snapshot.id, snapshot.data());
  }

  /**
   * Aliases of the project's LIVE articles — the published site index (`projects/{pid}/articleSiteIndex/{alias}`),
   * not the drafts: an article is live only once published and until retired. Read-only, sorted A–Z.
   */
  async listLiveAliases(projectId: string): Promise<string[]> {
    const snapshot = await getDocs(collection(this.firestore, `projects/${projectId}/${SITE_INDEX_COLLECTION}`));
    return snapshot.docs.map((docSnap) => docSnap.id).sort();
  }

  /**
   * The project's LIVE articles with their ids and titles: the published site index (alias + published title) joined
   * with the drafts (article id) the same way the article path-mode switch picks its articles (alias in the index,
   * draft not retired). A live alias without a draft is kept with `articleId: null`. Read-only, sorted by alias.
   */
  async listLiveArticles(projectId: string): Promise<LiveArticle[]> {
    const [index, drafts] = await Promise.all([
      getDocs(collection(this.firestore, `projects/${projectId}/${SITE_INDEX_COLLECTION}`)),
      this.listDrafts(projectId),
    ]);
    const draftByAlias = new Map(
      drafts.filter((draft) => draft.alias && draft.status !== 'retired').map((draft) => [draft.alias, draft]),
    );
    return index.docs
      .map((docSnap): LiveArticle => {
        const draft = draftByAlias.get(docSnap.id);
        const indexed = (docSnap.data() ?? {}) as { title?: unknown };
        const title = typeof indexed.title === 'string' && indexed.title ? indexed.title : draft?.title ?? '';
        return { articleId: draft?.id ?? null, alias: docSnap.id, title };
      })
      .sort((a, b) => (a.alias < b.alias ? -1 : a.alias > b.alias ? 1 : 0));
  }

  async cloneDraft(
    projectId: string,
    articleId: string,
    input: CloneArticleDraftInput = {},
  ): Promise<ArticleDraft> {
    const source = await this.getDraft(projectId, articleId);
    if (!source) {
      throw new Error(`Article draft "${articleId}" was not found`);
    }

    const collectionRef = collection(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`);
    const docRef = doc(collectionRef);
    const now = new Date().toISOString();
    const title = normalizeTitle(input.title || `${source.title} copy`);
    const defaultLanguage = normalizeLanguage(source.defaultLanguage);
    const alias = normalizeAlias(input.alias || `${slugify(source.alias || title)}-${docRef.id.slice(0, 6)}`);
    const flowDocument = retargetArticleFlowDocument(source.flowDocument, {
      projectId,
      articleId: docRef.id,
      title,
      alias,
      defaultLanguage,
    });

    const draft: ArticleDraft = {
      id: docRef.id,
      projectId,
      title,
      alias,
      status: 'draft',
      defaultLanguage,
      representationProfile: source.representationProfile,
      flowDocument,
      createdAt: now,
      updatedAt: now,
      createdBy: input.createdBy ?? CoreFirebaseService.getInstance().getCurrentUserId(),
      // Clone lands where the user is looking: inherit the source folder.
      folderId: source.folderId ?? null,
    };

    await setDoc(docRef, cleanForFirestore(draft));
    return draft;
  }

  async saveFlowDocument(
    projectId: string,
    articleId: string,
    flowDocument: FlowDocument,
    title?: string,
  ): Promise<ArticleDraft> {
    const existing = await this.getDraft(projectId, articleId);
    if (!existing) {
      throw new Error(`Article draft "${articleId}" was not found`);
    }

    const now = new Date().toISOString();
    const normalizedFlowDocument = normalizeArticleFlowDocument(flowDocument, {
      projectId,
      articleId,
      alias: existing.alias,
      defaultLanguage: existing.defaultLanguage,
    });
    const updated: ArticleDraft = {
      ...existing,
      // Content-derived titles stop the moment the user renames explicitly
      // (titleManuallySet via updateDetails) — a manual title survives every
      // subsequent editor save. Legacy drafts (field absent) keep deriving.
      title: existing.titleManuallySet
        ? existing.title
        : normalizeTitle(title || deriveArticleTitle(normalizedFlowDocument) || existing.title),
      status: 'draft',
      flowDocument: normalizedFlowDocument,
      updatedAt: now,
    };

    const docRef = doc(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`, articleId);
    // updateDoc replaces the flowDocument map wholesale, so keys removed in the
    // editor (e.g. clearing a screen's containerSize) actually get deleted —
    // setDoc(merge:true) keeps absent nested keys forever.
    await updateDoc(docRef, cleanForFirestore({
      title: updated.title,
      status: updated.status,
      flowDocument: updated.flowDocument,
      updatedAt: updated.updatedAt,
    }) as Record<string, unknown>);
    return updated;
  }

  async markPublished(
    projectId: string,
    articleId: string,
    input: SavePublishedArticleInput,
  ): Promise<ArticleDraft> {
    const existing = await this.getDraft(projectId, articleId);
    if (!existing) {
      throw new Error(`Article draft "${articleId}" was not found`);
    }

    const now = new Date().toISOString();
    const updated: ArticleDraft = {
      ...existing,
      alias: normalizeAlias(input.alias || existing.alias),
      status: 'published',
      publishedAt: now,
      publishedUrl: input.url,
      assetUrl: input.assetUrl,
      configUrl: input.configUrl,
      gatewayUrl: input.gatewayUrl,
      customDomainUrl: input.customDomainUrl,
      webShellVersion: input.webShellVersion,
      updatedAt: now,
    };

    const docRef = doc(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`, articleId);
    await setDoc(docRef, cleanForFirestore(updated), { merge: true });
    return updated;
  }

  /**
   * Rename and/or re-alias a draft. Partial write via updateDoc (dot-notation
   * for the embedded flowDocument copy of the alias — never setDoc merge).
   *
   * - Providing a title marks `titleManuallySet: true` — from then on the
   *   title is the user's and `saveFlowDocument` stops deriving it from
   *   content.
   * - The alias is slug-normalized on write. Uniqueness is NOT enforced here
   *   — validate with `isAliasUnique` in the UI before calling.
   * - Re-publishing under a new alias leaves the previously published URL
   *   live (only `articles retire` unpublishes) — surface that in dialog copy.
   */
  async updateDetails(
    projectId: string,
    articleId: string,
    input: UpdateArticleDetailsInput,
  ): Promise<ArticleDraft> {
    const existing = await this.getDraft(projectId, articleId);
    if (!existing) {
      throw new Error(`Article draft "${articleId}" was not found`);
    }

    const now = new Date().toISOString();
    const update: Record<string, unknown> = { updatedAt: now };

    const nextTitle = isNonEmptyString(input.title) ? normalizeTitle(input.title) : null;
    if (nextTitle) {
      update.title = nextTitle;
      update.titleManuallySet = true;
    }

    const nextAlias = isNonEmptyString(input.alias) ? normalizeAlias(input.alias) : null;
    const aliasChanged = Boolean(nextAlias && nextAlias !== existing.alias);
    if (nextAlias && aliasChanged) {
      update.alias = nextAlias;
      // The flowDocument carries a copy of the alias in metadata — keep it in
      // sync with a dot-path partial write so the next publish/save doesn't
      // ship a stale value.
      update['flowDocument.metadata.alias'] = nextAlias;
    }

    const nextEditorMode = input.editorMode === 'flow' || input.editorMode === 'screen'
      ? input.editorMode
      : null;
    if (nextEditorMode) {
      update.editorMode = nextEditorMode;
    }

    const docRef = doc(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`, articleId);
    await updateDoc(docRef, update);

    return {
      ...existing,
      title: nextTitle ?? existing.title,
      titleManuallySet: nextTitle ? true : existing.titleManuallySet,
      alias: nextAlias && aliasChanged ? nextAlias : existing.alias,
      editorMode: nextEditorMode ?? existing.editorMode,
      flowDocument: nextAlias && aliasChanged
        ? {
            ...existing.flowDocument,
            metadata: {
              ...((existing.flowDocument.metadata ?? {}) as Record<string, unknown>),
              alias: nextAlias,
            } as unknown as FlowDocument['metadata'],
          }
        : existing.flowDocument,
      updatedAt: now,
    };
  }

  /**
   * `true` when no OTHER draft in the project already uses the alias. The
   * candidate is slug-normalized first so the check matches what
   * `updateDetails` would actually store. In-memory over `listDrafts` — the
   * articles collection is small and already fetched wholesale by the list.
   */
  async isAliasUnique(
    projectId: string,
    alias: string,
    excludeArticleId?: string,
  ): Promise<boolean> {
    const normalized = normalizeAlias(alias);
    const drafts = await this.listDrafts(projectId);
    return !drafts.some(
      (draft) => draft.id !== excludeArticleId && draft.alias === normalized,
    );
  }

  /**
   * Move a draft to a folder (`null` = back to root). Partial write via
   * updateDoc — setDoc(merge:true) would be wrong here: it can never clear
   * keys and full-object merges resurrect stale nested state
   * (feedback_firestore_merge_bug).
   */
  async moveToFolder(
    projectId: string,
    articleId: string,
    folderId: string | null,
  ): Promise<void> {
    const docRef = doc(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`, articleId);
    await updateDoc(docRef, {
      folderId,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Delete a draft document. Published GCS artifacts and any web placements
   * created from this article are intentionally left untouched — published
   * funnels/pages keep serving until unpublished through their own flows.
   */
  async deleteDraft(projectId: string, articleId: string): Promise<void> {
    const docRef = doc(this.firestore, `projects/${projectId}/${COLLECTION_NAME}`, articleId);
    await deleteDoc(docRef);
  }
}

function createDefaultArticleFlowDocument(input: {
  projectId: string;
  articleId: string;
  title: string;
  alias: string;
  defaultLanguage: string;
}): FlowDocument {
  const screenId = `article_screen_${input.articleId}`;
  const step: StepNode = {
    id: screenId,
    name: input.title,
    tags: ['article'],
    screenType: 'FlexibleLayout' as StepNode['screenType'],
    position: { x: 0, y: 0 },
    content: {
      canvas: {
        background: {
          kind: 'solid',
          content: { color: DEFAULT_ARTICLE_BACKGROUND },
        },
        insets: { top: 0, end: 0, bottom: 0, start: 0 },
        webViewBackgroundColor: DEFAULT_ARTICLE_BACKGROUND,
      },
      animated: false,
      offlineFirst: true,
      flexibleLayout: {
        scrollable: true,
        insets: { top: 48, end: 32, bottom: 56, start: 32 },
        sections: [
          {
            id: 'article_headline',
            order: 0,
            kind: 'Text',
            label: 'Article headline',
            layout: {
              heightKind: 'auto',
              padding: { top: 32, end: 0, bottom: 20, start: 0 },
            },
            textContent: {
              title: localizedLabel(input.title, input.defaultLanguage, {
                align: 'center',
                fontSize: 38,
                fontWeight: 800,
                lineHeight: 44,
                color: '#172033',
              }),
              subtitle: localizedLabel(
                'Use Flexible Layout sections to write, style, and publish this article.',
                input.defaultLanguage,
                {
                  align: 'center',
                  fontSize: 17,
                  fontWeight: 400,
                  lineHeight: 26,
                  color: '#5B6472',
                }
              ),
            },
          },
          {
            id: 'article_body',
            order: 1,
            kind: 'Text',
            label: 'Article body',
            layout: {
              heightKind: 'auto',
              padding: { top: 24, end: 24, bottom: 24, start: 24 },
              margin: { top: 8, bottom: 8 },
              background: { kind: 'solid', color: '#FFFFFF' },
              cornerRadius: { uniform: 18 },
            },
            textContent: {
              title: localizedLabel('Start with the outcome', input.defaultLanguage, {
                align: 'start',
                fontSize: 22,
                fontWeight: 750,
                lineHeight: 28,
                color: '#172033',
              }),
              subtitle: localizedLabel(
                'Replace this section with the article narrative, add media, custom HTML, buttons, lists, or more text sections.',
                input.defaultLanguage,
                {
                  align: 'start',
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: 25,
                  color: '#394150',
                }
              ),
            },
          },
        ],
      },
    },
  } as StepNode;

  return {
    schemaVersion: '2.0.0',
    defaultLanguage: input.defaultLanguage as FlowDocument['defaultLanguage'],
    languages: [input.defaultLanguage as FlowDocument['defaultLanguage']],
    launchScreenId: screenId,
    screens: {
      [screenId]: step,
    },
    metadata: {
      kind: 'article',
      projectId: input.projectId,
      articleId: input.articleId,
      alias: input.alias,
      articlePresentation: DEFAULT_ARTICLE_PRESENTATION,
    } as unknown as FlowDocument['metadata'],
    webViewDefaultBackgroundColor: DEFAULT_ARTICLE_BACKGROUND,
  };
}

function normalizeArticleFlowDocument(
  flowDocument: FlowDocument,
  context: {
    projectId: string;
    articleId: string;
    alias: string;
    defaultLanguage: string;
  },
): FlowDocument {
  const normalized = cleanForFirestore(flowDocument) as FlowDocument;
  const defaultLanguage = normalizeLanguage(normalized.defaultLanguage || context.defaultLanguage);
  return {
    ...normalized,
    defaultLanguage: defaultLanguage as FlowDocument['defaultLanguage'],
    languages: Array.isArray(normalized.languages) && normalized.languages.length > 0
      ? normalized.languages
      : [defaultLanguage as FlowDocument['defaultLanguage']],
    metadata: {
      ...(normalized.metadata ?? {}),
      kind: 'article',
      projectId: context.projectId,
      articleId: context.articleId,
      alias: context.alias,
      articlePresentation: (normalized.metadata as Record<string, unknown> | undefined)?.articlePresentation ?? DEFAULT_ARTICLE_PRESENTATION,
    } as unknown as FlowDocument['metadata'],
    webViewDefaultBackgroundColor: normalized.webViewDefaultBackgroundColor ?? DEFAULT_ARTICLE_BACKGROUND,
  };
}

function retargetArticleFlowDocument(
  flowDocument: FlowDocument,
  context: {
    projectId: string;
    articleId: string;
    title: string;
    alias: string;
    defaultLanguage: string;
  },
): FlowDocument {
  const normalized = normalizeArticleFlowDocument(flowDocument, context);
  const launchScreen = normalized.screens?.[normalized.launchScreenId] as StepNode | undefined;
  if (!launchScreen) {
    return normalized;
  }

  const previousLaunchId = normalized.launchScreenId;
  const nextScreenId = `article_screen_${context.articleId}`;
  const screens = { ...(normalized.screens ?? {}) };
  delete screens[previousLaunchId];
  screens[nextScreenId] = {
    ...cleanForFirestore(launchScreen),
    id: nextScreenId,
    name: context.title,
  } as StepNode;

  // Multi-screen clones: edges targeting the launch screen live INSIDE other
  // screens' Trigger structures (`{ nextScreenId }`). The launch id changes on
  // clone, so every reference must follow — otherwise the clone keeps stale
  // edges pointing at the source article's launch screen.
  rewriteNextScreenIdReferences(screens, previousLaunchId, nextScreenId);

  return {
    ...normalized,
    launchScreenId: nextScreenId,
    screens,
    metadata: {
      ...(normalized.metadata ?? {}),
      kind: 'article',
      projectId: context.projectId,
      articleId: context.articleId,
      alias: context.alias,
      articlePresentation: (normalized.metadata as Record<string, unknown> | undefined)?.articlePresentation ?? DEFAULT_ARTICLE_PRESENTATION,
    } as unknown as FlowDocument['metadata'],
  };
}

/**
 * Rewrite every `nextScreenId` reference equal to `fromId` → `toId` across all
 * screens (in place — callers pass freshly cloned screen objects). V2 edges
 * live in many Trigger locations (actionBar, sections, sticky, options,
 * onCancelAction…), so this walks the whole content generically instead of
 * enumerating paths — same approach as the V2 paste id-rewrite.
 */
function rewriteNextScreenIdReferences(
  screens: Record<string, unknown>,
  fromId: string,
  toId: string,
): void {
  if (!fromId || fromId === toId) return;
  const visit = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!value || typeof value !== 'object') return;
    const record = value as Record<string, unknown>;
    if (record.nextScreenId === fromId) {
      record.nextScreenId = toId;
    }
    Object.values(record).forEach(visit);
  };
  Object.values(screens).forEach(visit);
}

function fromFirestore(projectId: string, id: string, data: Record<string, unknown>): ArticleDraft {
  return {
    ...(data as Omit<ArticleDraft, 'id' | 'projectId'>),
    id,
    projectId,
    status: ((data.status as ArticleDraftStatus | undefined) ?? 'draft'),
    defaultLanguage: normalizeLanguage(data.defaultLanguage as string | undefined),
    representationProfile: ((data.representationProfile as ArticleRepresentationProfile | undefined) ?? 'standard'),
    folderId: ((data.folderId as string | null | undefined) ?? null),
    // Legacy docs lack the flag → false → content-derived title (unchanged).
    titleManuallySet: Boolean(data.titleManuallySet),
    // Legacy docs lack the field → 'screen' (today's editor, unchanged).
    editorMode: data.editorMode === 'flow' ? 'flow' : 'screen',
  } as ArticleDraft;
}

function deriveArticleTitle(flowDocument: FlowDocument): string | null {
  const launchScreen = flowDocument.screens?.[flowDocument.launchScreenId] as any;
  const language = normalizeLanguage(flowDocument.defaultLanguage);
  const sections = launchScreen?.content?.flexibleLayout?.sections ?? [];
  const titleLabel = sections
    .filter((section: any) => section.kind === 'Text')
    .map((section: any) => section.textContent?.title)
    .find((label: any) => label?.translations && typeof label.translations === 'object');
  const translation = titleLabel?.translations?.[language];
  if (isNonEmptyString(translation)) return translation;
  const firstTranslation = Object.values(titleLabel?.translations ?? {}).find((value) => isNonEmptyString(value));
  if (isNonEmptyString(firstTranslation)) return firstTranslation;
  return isNonEmptyString(launchScreen?.name) ? launchScreen.name : null;
}

function localizedLabel(text: string, language: string, appearance: Record<string, unknown>) {
  return {
    translations: { [language]: text },
    kind: 'static',
    appearance,
  };
}

function normalizeTitle(title?: string): string {
  return isNonEmptyString(title) ? title.trim() : DEFAULT_ARTICLE_TITLE;
}

function normalizeLanguage(language?: string): string {
  return isNonEmptyString(language) ? language : DEFAULT_LANGUAGE;
}

function normalizeAlias(alias: string): string {
  const normalized = slugify(alias).slice(0, 50);
  return normalized.length >= 3 ? normalized : `article-${normalized || 'draft'}`;
}

/**
 * Public alias normalizer — the exact transform `updateDetails`/`createDraft`
 * apply on write. UI dialogs use it for live slug previews so what the user
 * sees is byte-identical to what gets stored.
 */
export function normalizeArticleAlias(alias: string): string {
  return normalizeAlias(alias);
}

function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
  return slug || 'article';
}

function cleanForFirestore<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
